import { useState } from "react";
import { motion, useReducedMotion } from "motion/react";

const PROGRESS_IDLE_SHADOW =
  "0px 1px 4px 0px var(--flo-primary-light-color), 0px 0.5px 2px -0.5px rgba(0, 0, 0, 0.4)";
const PROGRESS_CELEBRATE_SHADOW =
  "0px 0px 12px 3px var(--flo-primary-light-color), 0px 1px 4px 0px var(--flo-primary-light-color), 0px 0.5px 2px -0.5px rgba(0, 0, 0, 0.4)";

export const ProgressBar = ({ progress }: { progress: number }) => {
  const reducedMotion = useReducedMotion();
  // Render-time previous-value tracking — no effect, no timer.
  // Each increase bumps pulseId; the keyed glow overlay below remounts and fades out on its own.
  const [prevProgress, setPrevProgress] = useState(progress);
  const [pulseId, setPulseId] = useState(0);
  if (progress !== prevProgress) {
    if (progress > prevProgress) setPulseId((id) => id + 1);
    setPrevProgress(progress);
  }
  const clamped = Math.max(0, Math.min(100, progress));

  return (
    <div className="bg-black/[0.08] relative rounded-full h-[5px] w-full">
      <motion.div
        className="absolute h-[5px] left-0 top-0 rounded-full"
        initial={false}
        animate={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
        style={{ boxShadow: PROGRESS_IDLE_SHADOW }}
        transition={
          reducedMotion
            ? { duration: 0 }
            : { width: { type: "spring", stiffness: 140, damping: 22, mass: 0.7 } }
        }>
        <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none rounded-full"
            style={{
              backgroundImage:
                "linear-gradient(90deg, var(--flo-primary-dark-color) 0%, rgba(255, 255, 255, 0.3) 100%), linear-gradient(90deg, var(--flo-primary-dark-color) 0%, var(--flo-primary-dark-color) 100%)",
            }}
          />
          <div
            className="absolute h-[5px] left-0 right-0 top-0 opacity-30 mix-blend-plus-lighter"
            style={{
              backgroundImage:
                "linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 71.919%, rgb(255, 255, 255) 100%)",
            }}
          />
          {/* Shine sweep — 30% of the filled bar's width, loops left→right; the filled bar's
              animated width clips it, so it only ever travels up to the current progress */}
          {!reducedMotion && clamped > 0 && (
            <div
              className="absolute inset-y-0 left-0 pointer-events-none"
              // undo the fill's percentage so children are sized relative to the full track
              style={{ width: `${10000 / clamped}%` }}>
              <motion.div
                className="absolute top-0 h-full w-[30%] opacity-30 mix-blend-plus-lighter"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, rgba(255,255,255,0) 10.58%, #FFF 48.98%, rgba(255,255,255,0) 88.07%)",
                }}
                initial={{ left: "-30%" }}
                animate={{ left: ["-30%", "100%"] }}
                transition={{ duration: 1, ease: [0.27, 0.01, 0, 1.01], repeat: Infinity }}
              />
            </div>
          )}
          <div
            className="absolute inset-0 pointer-events-none rounded-[inherit]"
            style={{
              boxShadow:
                "inset 0px 0px 0px 0.4px var(--flo-primary-dark-color), inset 0px 0.8px 0px 0px rgba(255, 255, 255, 0.7)",
            }}
          />
        </div>
        {pulseId > 0 && !reducedMotion && (
          <motion.div
            key={pulseId}
            className="absolute inset-0 pointer-events-none rounded-[inherit]"
            style={{ boxShadow: PROGRESS_CELEBRATE_SHADOW }}
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.52, ease: "easeOut" }}
          />
        )}
      </motion.div>
    </div>
  );
};
